import { Table, message, Popover, Button } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { CheckCircleTwoTone } from "@ant-design/icons";
import EditButton from "../UI/EditButton/editbutton";
import { editPriceListDetails } from "../../api/priceLists";
import { PriceListDetailsInterface } from "./Interfaces";

type prop = {
  tableKey: number;
  priceListDetails: PriceListDetailsInterface[];
  currentPriceListId: number;
  setPriceListDetails: React.Dispatch<React.SetStateAction<PriceListDetailsInterface[]>>;
  setPriceListDetailsSearch: React.Dispatch<React.SetStateAction<PriceListDetailsInterface[]>>;
  isLoading: boolean
}

const PriceListDetails = ({ tableKey, priceListDetails, currentPriceListId, setPriceListDetails, setPriceListDetailsSearch, isLoading }: prop) => {

  const { t } = useTranslation("common");

  const [selectedEditRow, setSelectedEditRow] = useState<number | undefined>();
  const [editedPrice, setEditedPrice] = useState<number>(0);

  const { mutateAsync: editPriceListDetailsMutation } = useMutation(
    "editPriceListDetails",
    ({ currentPriceListId, id, editedPrice }: { currentPriceListId: number, id: number, editedPrice: number }) =>
      editPriceListDetails({ currentPriceListId, id, editedPrice }),
    {
      onMutate: () => {
        message.loading('loading...')
      },
      onSuccess: ({ data }) => {
        message.destroy()
        message.success(data.message)
        setPriceListDetails(data.data)
        setPriceListDetailsSearch(data.data)
        setSelectedEditRow(undefined)
      },
      onError: ({ response }) => {
        message.destroy()
        message.error(response.data.message)
      },
    }
  );

  const HandleEdit = (id: number, price: number) => {
    setSelectedEditRow(id)
    setEditedPrice(price)
  }

  const handleEditSave = (currentPriceListId: number, id: number, price: number, editedPrice: number) => {
    if (price === editedPrice) {
      setSelectedEditRow(undefined)
      return
    }
    editPriceListDetailsMutation({ currentPriceListId, id, editedPrice })
  }

  const columns: {}[] = [
    {
      title: `${t("category")}`,
      dataIndex: "category",
      key: "category",
      width: "10rem",
      render: (category: string, record: PriceListDetailsInterface) => {
        return <div>{record.item.category.name}</div>
      }
    },
    {
      title: `${t("item-name")}`,
      dataIndex: "name",
      key: "name",
      className: "table-header",
      align: "center",
      render: (name: string, record: PriceListDetailsInterface) => {
        return <div>{record.item.name}</div>
      }
    },
    {
      title: `${t("barcode")}`,
      dataIndex: "barcode",
      key: "barcode",
      className: "table-header",
      width: "9rem",
      align: "center",
      render: (barcode: string, record: PriceListDetailsInterface) => {
        return <div>{record.item.barcode}</div>
      }
    },     
    {
      title: `${t("cost")}`,
      dataIndex: "lastCost",
      key: "lastCost",
      width: "7rem",
      align: "center",
      render: (averageCost: number) => {
        return <div>{averageCost?.toFixed(2) ?? 0}</div>
      }
    },
    {
      title: `${t("price")}`,
      dataIndex: "newPrice",
      key: "newPrice",
      width: "7rem",
      align: "center",
      render: (newPrice: number, record: PriceListDetailsInterface) => {
        return selectedEditRow === record.id ?
          <input
            type="number"
            value={editedPrice ?? 0}
            onChange={(e) => setEditedPrice(Number(e.target.value))}
            placeholder={editedPrice?.toFixed(2)}
          />
          : <div>{newPrice?.toFixed(2) ?? 0}</div>
      }
    },
    {
      title: `${t("profit")}`,
      dataIndex: "profit",
      key: "profit",
      width: "7rem",
      align: "center",
      render: (profit: number) => {
        return <div>{profit?.toFixed(2) ?? 0}%</div>
      }
    },
    {
      title: `${t("action")}`,
      key: "action",
      width: "4rem",
      align: "center" as "center",
      render: (record: PriceListDetailsInterface) => (
        <>
          {selectedEditRow === record.id ? <Popover content={'Confirm changes'} mouseEnterDelay={0} mouseLeaveDelay={0}>
            <Button
              className="Button"
              type="text"
              icon={<CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: "14px" }} />}
              onClick={() => handleEditSave(currentPriceListId, record.id, record.newPrice, editedPrice)}
              size={"small"}
            />
          </Popover> : <EditButton onClick={() => HandleEdit(record?.id, record?.newPrice)} />}

        </>
      ),
    },
  ];

  return (
    <>
      <Table
        scroll={{ x: 1000 }}
        key={tableKey}
        columns={columns}
        dataSource={priceListDetails}
        rowKey={(record) => record.id}
        rowClassName={(record, index) =>
          index % 2 === 0
            ? "table-row-light table-row"
            : "table-row-dark table-row"
        }
        loading={isLoading}
      />
    </>
  );
};

export default PriceListDetails;
